import { useState, useEffect, useCallback } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import {
  Loader2,
  Check,
  Award,
  Settings as SettingsIcon,
  FileBadge,
  Plus,
  RotateCcw,
  Ban,
} from "lucide-react";
import {
  FormField,
  inputClass,
  buttonClass,
  SectionLoader,
  EmptyState,
  isNotFoundError,
  Modal,
} from "./shared";
import { fontCssFor } from "./PlatformPreview";

const DEFAULT_SETTINGS = {
  platform_name: "",
  primary_color: "#1E4FCF",
  accent_color: "#4d7ee0",
  logo: "",
  logoUrl: "",
  font_family: "Inter",
  certificate_enabled: true,
  certificate_title: "Certificaat van Voltooiing",
  certificate_subtitle: "heeft met succes de cursus afgerond",
  certificate_signature_name: "",
  certificate_signature_title: "CEO & Oprichter",
};

const SUBTABS = [
  { id: "settings", label: "Instellingen", icon: SettingsIcon },
  { id: "issued", label: "Uitgereikt", icon: FileBadge },
];

const Toggle = ({ checked, onChange, isDark, label, description }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex-1 min-w-0">
      <div className={`text-[12px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}>
        {label}
      </div>
      {description && (
        <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {description}
        </div>
      )}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 rounded-full transition-colors flex-shrink-0 ${
        checked ? "bg-[#1E4FCF]" : isDark ? "bg-white/[0.08]" : "bg-gray-200"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  </div>
);

const CertificatesTab = ({ isDark }) => {
  const [subtab, setSubtab] = useState("settings");

  return (
    <div className="space-y-4">
      <div
        className={`flex gap-1 p-1 rounded-lg w-fit ${
          isDark ? "bg-white/[0.04]" : "bg-black/[0.04]"
        }`}
      >
        {SUBTABS.map((s) => {
          const Icon = s.icon;
          const active = subtab === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSubtab(s.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                active
                  ? isDark
                    ? "bg-white/[0.08] text-white"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDark
                    ? "text-gray-500 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icon size={12} />
              {s.label}
            </button>
          );
        })}
      </div>

      {subtab === "settings" && <SettingsPanel isDark={isDark} />}
      {subtab === "issued" && <IssuedPanel isDark={isDark} />}
    </div>
  );
};

const CertificatePreview = ({ settings, isDark }) => {
  const fontCss = fontCssFor(settings.font_family);
  const logoSrc =
    settings.logoUrl ||
    (settings.logo?.startsWith("data:") || settings.logo?.startsWith("http")
      ? settings.logo
      : null);
  const studentName = "Anna Janssens";
  const courseName = "Introductie tot Marketing";

  return (
    <div className="flex flex-col gap-2">
      <span
        className={`text-[11px] font-medium uppercase tracking-wide ${
          isDark ? "text-gray-500" : "text-gray-500"
        }`}
      >
        Certificaat preview
      </span>
      <div
        className="rounded-lg overflow-hidden shadow-md border bg-white relative"
        style={{
          aspectRatio: "1.414 / 1",
          borderColor: isDark ? "rgba(255,255,255,0.06)" : "#e5e7eb",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${settings.primary_color}08, ${settings.accent_color}08)`,
          }}
        />
        <div
          className="absolute inset-3 border-2"
          style={{ borderColor: settings.primary_color + "40", borderRadius: 4 }}
        />
        <div
          className="absolute inset-4 border"
          style={{ borderColor: settings.primary_color + "20", borderRadius: 2 }}
        />
        <div className="relative h-full flex flex-col items-center justify-between p-6 text-center">
          <div className="flex items-center gap-2">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="h-8 object-contain" />
            ) : (
              <Award size={22} style={{ color: settings.primary_color }} />
            )}
            <span
              className="text-[11px] font-semibold tracking-widest uppercase"
              style={{ color: settings.primary_color, fontFamily: fontCss }}
            >
              {settings.platform_name || "Jouw Platform"}
            </span>
          </div>

          <div>
            <div
              className="text-[10px] uppercase tracking-[0.3em] mb-2"
              style={{ color: "#888", fontFamily: fontCss }}
            >
              Officieel
            </div>
            <h2
              className="text-[22px] font-bold mb-3 leading-tight"
              style={{ color: "#1a1a1a", fontFamily: fontCss }}
            >
              {settings.certificate_title || "Certificaat"}
            </h2>
            <div
              className="text-[11px] mb-2"
              style={{ color: "#555", fontFamily: fontCss }}
            >
              Hierbij verklaart dat
            </div>
            <div
              className="text-[18px] font-semibold mb-2"
              style={{ color: settings.primary_color, fontFamily: fontCss }}
            >
              {studentName}
            </div>
            <div
              className="text-[11px] mb-1"
              style={{ color: "#555", fontFamily: fontCss }}
            >
              {settings.certificate_subtitle || "heeft met succes de cursus afgerond"}
            </div>
            <div
              className="text-[13px] font-medium italic"
              style={{ color: "#1a1a1a", fontFamily: fontCss }}
            >
              "{courseName}"
            </div>
          </div>

          <div className="flex items-end justify-between w-full">
            <div className="text-left">
              <div
                className="border-b w-32 mb-1"
                style={{ borderColor: settings.primary_color }}
              />
              <div
                className="text-[9px] uppercase tracking-wide"
                style={{ color: "#555", fontFamily: fontCss }}
              >
                Datum
              </div>
              <div
                className="text-[10px]"
                style={{ color: "#1a1a1a", fontFamily: fontCss }}
              >
                {new Date().toLocaleDateString("nl-BE")}
              </div>
            </div>
            <div className="text-right">
              <div
                className="border-b w-32 mb-1 ml-auto"
                style={{ borderColor: settings.primary_color }}
              />
              <div
                className="text-[10px] font-semibold"
                style={{ color: "#1a1a1a", fontFamily: fontCss }}
              >
                {settings.certificate_signature_name || "Naam ondertekenaar"}
              </div>
              <div
                className="text-[9px]"
                style={{ color: "#666", fontFamily: fontCss }}
              >
                {settings.certificate_signature_title || "Functie"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsPanel = ({ isDark }) => {
  const { getSettings, updateSettings } = useLearningPlatform();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [original, setOriginal] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const merged = { ...DEFAULT_SETTINGS, ...(data || {}) };
        setSettings(merged);
        setOriginal(merged);
      } catch (e) {
        if (!isNotFoundError(e)) setError("Kon instellingen niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getSettings]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);

  const update = (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setSavedAt(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await updateSettings(settings);
      const merged = { ...DEFAULT_SETTINGS, ...(data || settings) };
      setSettings(merged);
      setOriginal(merged);
      setSavedAt(Date.now());
    } catch (e) {
      setError("Kon niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(original);
    setError(null);
    setSavedAt(null);
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6">
      <div className="space-y-6 min-w-0">
        <div
          className={`rounded-lg border p-4 ${
            isDark
              ? "bg-white/[0.02] border-white/[0.06]"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <Toggle
            checked={!!settings.certificate_enabled}
            onChange={(v) => update({ certificate_enabled: v })}
            isDark={isDark}
            label="Activeer certificaten"
            description="Studenten ontvangen automatisch een certificaat na voltooiing"
          />
        </div>

        <FormField label="Titel" hint="Bv. 'Certificaat van Voltooiing'" isDark={isDark}>
          <input
            type="text"
            value={settings.certificate_title}
            onChange={(e) => update({ certificate_title: e.target.value })}
            className={inputClass(isDark)}
          />
        </FormField>

        <FormField label="Ondertitel" hint="Zin onder de naam van de student" isDark={isDark}>
          <input
            type="text"
            value={settings.certificate_subtitle}
            onChange={(e) => update({ certificate_subtitle: e.target.value })}
            placeholder="heeft met succes de cursus afgerond"
            className={inputClass(isDark)}
          />
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Naam ondertekenaar" isDark={isDark}>
            <input
              type="text"
              value={settings.certificate_signature_name}
              onChange={(e) =>
                update({ certificate_signature_name: e.target.value })
              }
              placeholder="bv. Jan De Vries"
              className={inputClass(isDark)}
            />
          </FormField>
          <FormField label="Functie" isDark={isDark}>
            <input
              type="text"
              value={settings.certificate_signature_title}
              onChange={(e) =>
                update({ certificate_signature_title: e.target.value })
              }
              placeholder="bv. CEO & Oprichter"
              className={inputClass(isDark)}
            />
          </FormField>
        </div>

        {error && (
          <div
            className={`text-[12px] px-3 py-2 rounded-md ${
              isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            }`}
          >
            {error}
          </div>
        )}

        <div
          className={`flex items-center justify-end gap-2 pt-2 border-t ${
            isDark ? "border-white/[0.06]" : "border-gray-100"
          }`}
        >
          {savedAt && !dirty && (
            <span
              className={`flex items-center gap-1 text-[12px] mr-auto ${
                isDark ? "text-emerald-400" : "text-emerald-600"
              }`}
            >
              <Check size={13} /> Opgeslagen
            </span>
          )}
          <button
            type="button"
            onClick={handleCancel}
            disabled={!dirty || saving}
            className={buttonClass(isDark, "ghost")}
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className={buttonClass(isDark, "primary")}
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            Opslaan
          </button>
        </div>
      </div>

      <div className="lg:sticky lg:top-4 lg:self-start">
        <CertificatePreview settings={settings} isDark={isDark} />
      </div>
    </div>
  );
};

const IssuedPanel = ({ isDark }) => {
  const {
    getCertificates,
    issueCertificate,
    revokeCertificate,
    getCourses,
  } = useLearningPlatform();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getCertificates();
      setCertificates(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!isNotFoundError(e)) setError("Kon certificaten niet laden");
    } finally {
      setLoading(false);
    }
  }, [getCertificates]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (cert) => {
    try {
      await revokeCertificate(cert.id);
      await load();
    } catch (e) {
      setError("Kon niet intrekken");
    }
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            Uitgereikte certificaten
          </h3>
          <p className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {certificates.length} {certificates.length === 1 ? "certificaat" : "certificaten"}
          </p>
        </div>
        <button
          onClick={() => setIssueOpen(true)}
          className={buttonClass(isDark, "primary")}
        >
          <Plus size={13} /> Reik certificaat uit
        </button>
      </div>

      {error && (
        <div className={`text-[12px] px-3 py-2 rounded-md ${isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"}`}>
          {error}
        </div>
      )}

      {certificates.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Nog geen certificaten uitgereikt"
          description="Certificaten worden automatisch aangemaakt zodra een student alle lessen van een cursus afrondt. Je kunt er ook handmatig één uitreiken."
        />
      ) : (
        <div
          className={`rounded-lg border overflow-hidden ${
            isDark ? "border-white/[0.06]" : "border-gray-200"
          }`}
        >
          <table className="w-full text-[12px]">
            <thead
              className={`text-left ${
                isDark ? "bg-white/[0.02] text-gray-500" : "bg-gray-50 text-gray-500"
              }`}
            >
              <tr>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Nummer</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Student</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Cursus</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Uitgereikt</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((cert) => (
                <tr
                  key={cert.id}
                  className={`border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                >
                  <td className="px-3 py-2 font-mono">{cert.certificate_number}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                          isDark ? "bg-white/[0.08] text-gray-300" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {(cert.student_name || cert.student_email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col leading-tight">
                        <span className={isDark ? "text-gray-200" : "text-gray-900"}>
                          {cert.student_name || "—"}
                        </span>
                        <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          {cert.student_email}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">{cert.course_title}</td>
                  <td className="px-3 py-2">
                    {cert.issued_at
                      ? new Date(cert.issued_at).toLocaleDateString("nl-BE")
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {cert.revoked_at ? (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                          isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
                        }`}
                      >
                        <Ban size={10} /> Ingetrokken
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                          isDark
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        <Check size={10} /> Geldig
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!cert.revoked_at && (
                      <button
                        onClick={() => handleRevoke(cert)}
                        className={`p-1.5 rounded ${
                          isDark
                            ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                            : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                        }`}
                        title="Trek certificaat in"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {issueOpen && (
        <IssueModal
          isDark={isDark}
          onClose={() => setIssueOpen(false)}
          getCourses={getCourses}
          issue={issueCertificate}
          onIssued={() => {
            setIssueOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
};

const IssueModal = ({ isDark, onClose, getCourses, issue, onIssued }) => {
  const { getStudents } = useLearningPlatform();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([getStudents(), getCourses()]);
        setStudents(Array.isArray(s) ? s : []);
        setCourses(Array.isArray(c) ? c : []);
      } catch (e) {
        setError("Kon studenten of cursussen niet laden");
      } finally {
        setLoading(false);
      }
    })();
  }, [getStudents, getCourses]);

  const handleIssue = async () => {
    if (!studentId || !courseId) return;
    setBusy(true);
    setError(null);
    try {
      await issue({ student_id: studentId, course_id: courseId, force: true });
      onIssued();
    } catch (e) {
      setError(e?.response?.data?.message || "Kon certificaat niet uitreiken");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? undefined : onClose} title="Certificaat uitreiken" isDark={isDark}>
      {loading ? (
        <SectionLoader isDark={isDark} />
      ) : (
        <div className="space-y-4">
          <FormField label="Student" isDark={isDark}>
            <select
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              className={inputClass(isDark)}
            >
              <option value="">— Kies een student —</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.email}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Cursus" isDark={isDark}>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className={inputClass(isDark)}
            >
              <option value="">— Kies een cursus —</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </FormField>
          {error && (
            <div className={`text-[12px] px-3 py-2 rounded-md ${isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"}`}>
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6" }}>
            <button onClick={onClose} disabled={busy} className={buttonClass(isDark, "ghost")}>
              Annuleer
            </button>
            <button
              onClick={handleIssue}
              disabled={busy || !studentId || !courseId}
              className={buttonClass(isDark, "primary")}
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              Uitreiken
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CertificatesTab;
